import React, { useEffect, useState } from 'react';
import { discord, DiscordStatus } from '../../services/discord';
import { LoadingSpinner } from '../common/LoadingSpinner';

// Account -> Discord: link this account to a Discord user so the community
// server's @Pro role tracks the subscription automatically (granted while
// active or trialing, removed on lapse — never kicked). The callback bounces
// back to /dashboard with #discord=<outcome>, which we surface as a one-time
// notice here.
const CALLBACK_NOTICES: Record<string, { ok: boolean; text: string }> = {
  linked: { ok: true, text: 'Discord connected. If you have Pro, the role lands within a few seconds.' },
  already_linked_elsewhere: { ok: false, text: 'That Discord account is already connected to a different LACloud account.' },
  link_failed: { ok: false, text: 'Discord connection failed. Please try again.' },
  access_denied: { ok: false, text: 'Discord connection was cancelled.' },
};

const readCallbackNotice = (): { ok: boolean; text: string } | null => {
  const m = window.location.hash.match(/discord=([a-z_]+)/);
  if (!m) return null;
  // Consume the fragment so a refresh doesn't re-toast.
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return CALLBACK_NOTICES[m[1]] ?? { ok: false, text: 'Discord connection failed. Please try again.' };
};

export const DiscordSettings: React.FC = () => {
  const [status, setStatus] = useState<DiscordStatus | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    discord.status().then(setStatus).catch(() => setStatus({ available: false, linked: false, username: null }));

  useEffect(() => {
    setNotice(readCallbackNotice());
    load();
  }, []);

  const connect = async () => {
    setNotice(null); setBusy(true);
    try { window.location.href = await discord.linkStart(); }
    catch (e: any) {
      setNotice({ ok: false, text: e.response?.data?.detail || 'Could not start Discord connection.' });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setNotice(null); setBusy(true);
    try { await discord.unlink(); await load(); }
    catch { setNotice({ ok: false, text: 'Could not disconnect. Please try again.' }); }
    finally { setBusy(false); }
  };

  // Server not configured for linking yet — hide the panel entirely rather
  // than show a dead button.
  if (status && !status.available && !status.linked) return null;

  return (
    <div className="auth-panel" style={{ marginTop: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 style={{ marginBottom: 0 }}>Discord</h2>
        {status && (
          <span className={`auth-badge${status.linked ? ' ok' : ''}`}>
            {status.linked ? 'Connected' : 'Not connected'}
          </span>
        )}
      </div>

      {notice && (
        <div className={notice.ok ? 'auth-note' : 'auth-error'} role={notice.ok ? 'status' : 'alert'}
             style={{ textAlign: 'left', marginTop: 10 }}>
          {notice.text}
        </div>
      )}

      {status === null && <div style={{ padding: '10px 0' }}><LoadingSpinner size="sm" /></div>}

      {status && !status.linked && (
        <>
          <p className="auth-note" style={{ textAlign: 'left', margin: '4px 0 12px' }}>
            Connect your Discord account and the <b>@Pro</b> role in our community server follows your
            subscription automatically — including the free trial. Not on the server yet?{' '}
            <a href="https://localareacloud.com/discord" target="_blank" rel="noreferrer">Join here</a>.
          </p>
          <button type="button" className="auth-btn" style={{ width: 'auto' }} disabled={busy} onClick={connect}>
            {busy ? 'Opening Discord…' : 'Connect Discord'}
          </button>
        </>
      )}

      {status && status.linked && (
        <>
          <div className="auth-row">
            <span className="auth-key">Connected as</span>
            <span className="auth-val">{status.username ?? 'Discord user'}</span>
          </div>
          <p className="auth-note" style={{ textAlign: 'left', margin: '4px 0 12px' }}>
            Disconnecting removes the @Pro role from your Discord account.
          </p>
          <button type="button" className="auth-btn auth-btn-ghost" style={{ width: 'auto' }}
                  disabled={busy} onClick={disconnect}>
            {busy ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </>
      )}
    </div>
  );
};
